import { Localize } from '@/i18n/Localize';
import { sharedPortuguese } from '@/i18n/shared';
import React from 'react';
import { useSearchParams } from 'react-router-dom';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import SEO from '@/components/SEO';
import SmsOptInForm from '@/components/SmsOptInForm';
import SectionHeading from '@/components/SectionHeading';
import { BellRing, ShieldCheck, MessageSquareText } from 'lucide-react';
import FaqAnswer from '@/components/FaqAnswer';

const faqs = [
  {
    question: 'What kind of text messages will GizmoData send me?',
    answer:
      'Automated text messages about Hardcharger and GizmoEdge product releases, promotional offers, and important updates — the announcements worth interrupting you for, like major releases and limited-time pricing.',
  },
  {
    question: 'How many messages will I receive?',
    answer:
      'Up to 4 messages per month. Message and data rates may apply depending on your mobile phone service plan.',
  },
  {
    question: 'How do I stop receiving messages?',
    answer:
      'Reply STOP to any message to unsubscribe at any time, or reply HELP for help. You can re-subscribe later on this page.',
  },
  {
    question: 'Will GizmoData share my phone number?',
    answer:
      'No. Your phone number and SMS opt-in information are never sold or shared with third parties for their marketing purposes. See our Privacy Policy for details.',
  },
];

const SmsAlertsPage = () => {
  const [searchParams] = useSearchParams();
  // Which CTA/surface sent the visitor here (set via /sms-alerts?from=...) —
  // recorded with the opt-in. Defaults to 'sms_alerts_page'.
  const source = searchParams.get('from') || 'sms_alerts_page';

  return <Localize messages={sharedPortuguese}>{(
    <div className="flex min-h-screen flex-col">
      <SEO
        title="GizmoData SMS Alerts | Text Message Updates"
        description="Opt in to GizmoData SMS alerts: text message updates about Hardcharger and GizmoEdge releases, promotional offers, and important news. Up to 4 messages per month; reply STOP any time."
        url="https://gizmodata.com/sms-alerts"
        keywords="GizmoData SMS alerts, Hardcharger text updates, GizmoData text messages"
        faqSchema={faqs}
        breadcrumbSchema={[
          { name: 'Home', url: 'https://gizmodata.com/' },
          { name: 'SMS Alerts', url: 'https://gizmodata.com/sms-alerts' },
        ]}
      />
      <Navbar />
      <main className="flex-grow">
        {/* Hero */}
        <section className="hero-gradient-alt relative overflow-hidden text-white">
          <div className="absolute inset-0 overflow-hidden">
            <div className="absolute -top-40 -right-40 h-96 w-96 rounded-full bg-gizmo-500/10 blur-3xl" />
            <div className="absolute -bottom-40 -left-40 h-[28rem] w-[28rem] rounded-full bg-gizmo-400/15 blur-3xl" />
          </div>
          <div className="relative z-10 mx-auto max-w-3xl px-4 py-20 text-center sm:px-6 md:py-28 lg:px-8">
            <h1 className="text-4xl font-bold leading-tight md:text-5xl">GizmoData SMS Alerts</h1>
            <p className="mt-4 text-lg text-gray-200 md:text-xl">
              Be first to hear about Hardcharger and GizmoEdge releases, promotional offers, and
              important updates — straight to your phone. Up to 4 texts per month, stop any time.
            </p>
          </div>
        </section>

        {/* Why + form */}
        <section className="bg-background py-16 md:py-24">
          <div className="mx-auto grid max-w-7xl grid-cols-1 gap-12 px-4 sm:px-6 lg:grid-cols-2 lg:px-8">
            {/* Left: what you get */}
            <div>
              <SectionHeading anchor="why-sms" withId className="text-2xl md:text-3xl font-bold mb-4 text-foreground">
                Why sign up?
              </SectionHeading>
              <p className="mb-8 max-w-md text-lg text-muted-foreground">
                We save texts for the announcements that matter — no drip campaigns, no noise.
              </p>
              <ul className="space-y-4 text-sm text-muted-foreground">
                <li className="flex items-start gap-3">
                  <BellRing className="mt-0.5 h-5 w-5 flex-shrink-0 text-gizmo-600 dark:text-gizmo-400" />
                  <span>
                    <strong className="text-foreground">Release alerts first.</strong> Major
                    Hardcharger and GizmoEdge releases and important updates, the moment they ship.
                  </span>
                </li>
                <li className="flex items-start gap-3">
                  <MessageSquareText className="mt-0.5 h-5 w-5 flex-shrink-0 text-gizmo-600 dark:text-gizmo-400" />
                  <span>
                    <strong className="text-foreground">Low volume.</strong> Up to 4 messages per
                    month — and usually fewer.
                  </span>
                </li>
                <li className="flex items-start gap-3">
                  <ShieldCheck className="mt-0.5 h-5 w-5 flex-shrink-0 text-gizmo-600 dark:text-gizmo-400" />
                  <span>
                    <strong className="text-foreground">You're in control.</strong> Reply STOP to
                    any message to unsubscribe instantly, or HELP for help. We never sell or share
                    your number.
                  </span>
                </li>
              </ul>
            </div>

            {/* Right: the opt-in form */}
            <div className="rounded-xl border border-border bg-card p-6 shadow-sm md:p-8">
              <h2 className="mb-6 text-xl font-semibold text-foreground">Get text alerts</h2>
              <SmsOptInForm source={source} />
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="border-t border-border bg-muted/30 py-16 md:py-24">
          <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8">
            <SectionHeading anchor="sms-faq" withId className="mb-8 text-2xl md:text-3xl font-bold text-foreground">
              Frequently asked
            </SectionHeading>
            <div className="space-y-6">
              {faqs.map((faq) => (
                <div key={faq.question} className="rounded-2xl border border-border bg-card p-6 shadow-sm">
                  <h3 className="mb-2 text-lg font-semibold text-foreground">{faq.question}</h3>
                  <p className="text-sm leading-relaxed text-muted-foreground"><FaqAnswer text={faq.answer} gaLocation="sms_faq" /></p>
                </div>
              ))}
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  )}</Localize>;
};

export default SmsAlertsPage;
